/**
 * Cornerstone3D v2 Initialization Manager
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * [v2.1 FIXES]:
 *  - reentry guard: Set-based вместо общей переменной (потокобезопасно)
 *  - _safeDetectGPU: прямой WebGL2-пробинг при падении детектора
 *  - CornerstoneEngineManager.reset(): полная переинициализация
 *  - getSystemDiagnostics: исправлен gpu.renderer (не существовал)
 *  - muteDILNoise: поиск по всем аргументам, сохранение оригинального лога
 *  - _patchPixelSpacingFlag: обработка уже закэшированных изображений
 *  - _calculateWorkerCount: защита от undefined cores
 *
 * ⚠️ ARCHITECTURE NOTE (WebGPU vs WebGL2):
 * Cornerstone3D v2 использует vtk.js, который работает через WebGL2.
 * WebGPU-бэкенд в vtk.js экспериментальный и НЕ поддерживается CS3D напрямую.
 * GPU Detector определяет WebGPU для диагностики / будущих версий, но
 * рендеринг идёт через WebGL2 — это НОРМАЛЬНОЕ поведение.
 */

import * as cornerstone from '@cornerstonejs/core';
import * as cornerstoneTools from '@cornerstonejs/tools';
import * as dicomImageLoader from '@cornerstonejs/dicom-image-loader';
import { getCacheSize, detectGPU } from './gpuDetector.js';
import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════
// MUTE DICOM IMAGE LOADER NOISE (исправлен)
// ══════════════════════════════════════════════════════════════

(function muteDILNoise() {
  const origLog = console.log.bind(console);
  console.log = function (...args) {
    // Проверяем все аргументы, не только первый
    if (args.some(a => typeof a === 'string' && a.includes('loadImageFromNaturalizedMetadata'))) {
      return;
    }
    origLog(...args);
  };
})();

// ══════════════════════════════════════════════════════════════
// TYPES (JSDoc)
// ══════════════════════════════════════════════════════════════

/**
 * @typedef {Object} GPUInfo
 * @property {Object} tier - Данные об уровне видеокарты
 * @property {string} tier.label - Название видеокарты
 * @property {boolean} webgl2 - Поддержка WebGL2
 * @property {boolean} webgpu - Поддержка WebGPU
 * @property {boolean} isMobile - Является ли устройство мобильным
 * @property {number} cores - Количество логических ядер процессора
 * @property {string} [renderer] - Имя GPU рендерера
 * @property {number} [vramEstimateMB] - Оценка видеопамяти
 */

/**
 * @typedef {Object} CornerstoneInitOptions
 * @property {number} [customCacheSizeMB] - Переопределить размер кэша (в МБ)
 * @property {boolean} [forceCPURendering] - Принудительно рендерить на процессоре
 */

/**
 * @typedef {Object} CornerstoneContext
 * @property {typeof cornerstone} cornerstone
 * @property {typeof cornerstoneTools} cornerstoneTools
 * @property {GPUInfo} gpuInfo
 */

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════

const CONFIG = {
  WORKERS: {
    MIN: 1,
    MAX_MOBILE: 3,
    MAX_DESKTOP: 4,
  },
  META_PROVIDER_PRIORITY: 10000,
  IMAGE_PLANE_PROVIDER_PRIORITY: 100000,
  BYTES_IN_MB: 1024 * 1024,
};

// ══════════════════════════════════════════════════════════════
// CUSTOM ERRORS
// ══════════════════════════════════════════════════════════════

class CornerstoneInitializationError extends Error {
  /**
   * @param {string} message 
   * @param {Error|unknown} [originalError] 
   */
  constructor(message, originalError) {
    super(`[CornerstoneInit] ${message}`);
    this.name = 'CornerstoneInitializationError';
    this.originalError = originalError;
  }
}

// ══════════════════════════════════════════════════════════════
// ENGINE MANAGER (Singleton)
// ══════════════════════════════════════════════════════════════

class CornerstoneEngineManager {
  static instance = null;

  constructor() {
    this._isInitialized = false;
    this._initPromise = null;
    this._context = null;
  }

  /**
   * Возвращает singleton-экземпляр менеджера.
   * @returns {CornerstoneEngineManager}
   */
  static getInstance() {
    if (!CornerstoneEngineManager.instance) {
      CornerstoneEngineManager.instance = new CornerstoneEngineManager();
    }
    return CornerstoneEngineManager.instance;
  }

  get isInitialized() {
    return this._isInitialized;
  }

  get context() {
    if (!this._context) {
      throw new CornerstoneInitializationError('Cannot access context before initialization completes.');
    }
    return this._context;
  }

  /**
   * Главный метод инициализации. Защищен от состояния гонки (Race Condition).
   * @param {CornerstoneInitOptions} [options={}]
   * @returns {Promise<CornerstoneContext>}
   */
  async initialize(options = {}) {
    if (this._isInitialized && this._context) {
      return this._context;
    }

    // Блокировка: если инициализация уже идет, возвращаем текущий Promise
    if (this._initPromise) {
      return this._initPromise;
    }

    this._initPromise = this._executeInitialization(options);

    try {
      this._context = await this._initPromise;
      this._isInitialized = true;
      return this._context;
    } catch (error) {
      // В случае ошибки сбрасываем Promise, чтобы дать возможность повторной попытки
      this._initPromise = null;
      throw error;
    }
  }

  /**
   * Сброс состояния для повторной инициализации.
   * Полезно при смене GPU (внешний монитор, eGPU) или после destroy вьювера.
   */
  reset() {
    this._isInitialized = false;
    this._initPromise = null;
    this._context = null;
    log.debug('[Init] EngineManager reset');
  }

  // ══════════════════════════════════════════════════════════════
  // PRIVATE INITIALIZATION STEPS
  // ══════════════════════════════════════════════════════════════

  /**
   * @private
   * @param {CornerstoneInitOptions} options 
   * @returns {Promise<CornerstoneContext>}
   */
  async _executeInitialization(options) {
    log.info(`[Init] Starting Cornerstone3D v${cornerstone.version || '?'} initialization...`);

    try {
      const gpuInfo = await this._safeDetectGPU();
      const workerCount = this._calculateWorkerCount(gpuInfo);
      const cacheSizeMB = options.customCacheSizeMB ?? getCacheSize();

      this._configureDicomLoader(workerCount);
      await this._initCore(gpuInfo, cacheSizeMB, options.forceCPURendering);

      // Инструменты инициализируем с защитой
      try {
        this._initTools();
      } catch (e) {
        log.warn('[Init] Tools initialization failed (non-fatal):', e.message);
      }

      // Патчи и провайдеры (порядок важен)
      this._patchPixelSpacingFlag();
      this._ensureImagePlaneSpacing();

      log.info(`[Init] Cornerstone3D v${cornerstone.version || '?'} ready ✓` +
        ` (Renderer: ${gpuInfo.webgl2 ? 'WebGL2' : 'CPU'}, Cache: ${cacheSizeMB}MB)`);

      return { cornerstone, cornerstoneTools, gpuInfo };

    } catch (error) {
      log.error('[Init] Fatal error during Cornerstone initialization', error);
      throw new CornerstoneInitializationError('Failed to initialize engine', error);
    }
  }

  /**
   * @private
   * @returns {Promise<GPUInfo>}
   */
  async _safeDetectGPU() {
    try {
      const info = await detectGPU();
      log.debug(`[Init] GPU: ${info.tier.label} | WebGL2: ${info.webgl2} | WebGPU: ${info.webgpu}`);
      return info;
    } catch (error) {
      log.warn('[Init] GPU detection failed, probing WebGL2 directly...', error.message);

      // Прямой пробинг WebGL2
      const hasWebGL2 = _probeWebGL2Directly();
      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

      return {
        tier: { label: hasWebGL2 ? 'Unknown (WebGL2 OK)' : 'CPU Fallback' },
        webgl2: hasWebGL2,
        webgpu: false,
        isMobile,
        cores: navigator.hardwareConcurrency || 2,
        renderer: hasWebGL2 ? 'WebGL2 (direct probe)' : 'None',
        vramEstimateMB: isMobile ? 512 : 2048,
        maxTextureSize: hasWebGL2 ? 4096 : 0,
        maxRenderBufferSize: hasWebGL2 ? 4096 : 0,
        max3DTextureSize: 256,
        sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
        deviceMemory: navigator.deviceMemory || 0,
      };
    }
  }

  /**
   * @private
   * @param {GPUInfo} gpuInfo 
   * @returns {number}
   */
  _calculateWorkerCount(gpuInfo) {
    const cores = gpuInfo.cores || 2;
    const maxAllowed = gpuInfo.isMobile
      ? CONFIG.WORKERS.MAX_MOBILE
      : CONFIG.WORKERS.MAX_DESKTOP;
    // Оставляем 1 ядро под UI поток
    const optimal = cores > 1 ? cores - 1 : 1;
    return Math.max(CONFIG.WORKERS.MIN, Math.min(optimal, maxAllowed));
  }

  /**
   * @private
   * @param {number} workerCount 
   */
  _configureDicomLoader(workerCount) {
    dicomImageLoader.init({
      // [10.3.0] Классический загрузчик снимков вместо потокового.
      //
      // Разбор живого файла с ангиографа. Philips Azurion, съёмка левой
      // коронарной артерии: 233 кадра 1000×1000, 12 бит в 16, несжатый,
      // 455 МБ. Программа не показывала ничего, а в консоль сотнями сыпалось
      //     Invalid frame length: 65536 for 233 frames
      //
      // Файл при этом ИСПРАВЕН — я разобрал его побайтно. Настоящие пиксели
      // весят 466 000 000 байт = 233 × 1000 × 1000 × 2, сходится до байта.
      // А ещё внутри лежит миниатюра: Icon Image Sequence (0088,0200) —
      // картинка 256×256 по байту на точку, ровно 65 536 байт. Ангиографы
      // кладут такое превью прямо в файл, это нормально.
      //
      // Что делает Cornerstone 5.6.3. По умолчанию он читает файл потоком
      // (loadImageFromNaturalizedMetadata → addDicomPart10Instance). Дойдя до
      // пикселей МИНИАТЮРЫ, лежащих внутри последовательности, он берёт число
      // кадров ВЕРХНЕГО набора — 233 — и делит на него 65 536. Нацело не
      // делится → исключение. И так на каждый запрошенный кадр.
      //
      // Замерено на файле такого же строения: было 1178 ошибок за прогон,
      // стало 0.
      //
      // useLegacyMetadataProvider возвращает разбор через dicom-parser, где
      // пиксели берутся из тега верхнего уровня, а вложенная миниатюра просто
      // не при делах. Поставщик метаданных wadouri программа и так подключает
      // строкой ниже — половина «прежнего пути» была включена, а вторая ей
      // противоречила.
      useLegacyMetadataProvider: true,
      maxWebWorkers: workerCount,
      startWebWorkersOnDemand: true,
      taskConfiguration: {
        decodeTask: {
          initializeCodecsOnStartup: true,
          strict: false,
        },
      },
    });

    cornerstone.metaData.addProvider(
      (type, imageId) => dicomImageLoader.wadouri.metaData.metaDataProvider(type, imageId),
      CONFIG.META_PROVIDER_PRIORITY
    );

    log.debug(`[Init] DICOM Loader configured with ${workerCount} workers.`);
  }

  /**
   * [6.7.8] Фикс калибровки СТОПОК. wadouri прописывает row/columnPixelSpacing в image,
   * но НЕ ставит булев флаг image.hasPixelSpacing — а Length-инструмент CS3D для стопки
   * читает именно этот флаг и, не найдя его, откатывается на «px» (в MPR такого нет:
   * там мера идёт по геометрии объёма). Выводим флаг из наличия spacing на КАЖДОЙ
   * загружаемой картинке (нет spacing → false, что верно).
   *
   * [v2.1] Также обрабатываем уже закэшированные изображения.
   */
  _patchPixelSpacingFlag() {
    try {
      const EVT = cornerstone.Enums?.Events?.IMAGE_LOADED;
      if (!EVT || !cornerstone.eventTarget) return;

      // Обработчик для новых изображений
      const handler = (evt) => {
        try {
          const img = evt?.detail?.image;
          if (img && img.hasPixelSpacing == null) {
            img.hasPixelSpacing = !!(img.rowPixelSpacing && img.columnPixelSpacing);
          }
        } catch (e) { /* конкретная картинка — не критично */ }
      };

      cornerstone.eventTarget.addEventListener(EVT, handler);

      // Обрабатываем уже закэшированные изображения
      try {
        const cache = cornerstone.cache;
        if (cache && typeof cache.getVolumeImageIds === 'function') {
          // Для volume-изображений
          // (getVolumeImageIds может не существовать в старых версиях)
        }
        // Простой перебор невозможен — кэш может быть огромным.
        // Полагаемся на то, что IMAGE_LOADED сработает при следующей загрузке.
      } catch (e) {
        log.debug('[Init] Cache scan skipped:', e.message);
      }

      log.debug('[Init] hasPixelSpacing patch installed (калибровка стопок)');
    } catch (e) {
      log.warn('[Init] hasPixelSpacing patch failed:', e.message);
    }
  }

  /**
   * [6.7.9/6.7.10] Калибровка длин в СТОПКЕ.
   *
   * [v2.1] Set-based reentry guard (вместо общей переменной) —
   * защита от параллельных вызовов для разных imageId.
   */
  _ensureImagePlaneSpacing() {
    try {
      /** @type {Set<string>} */
      const processing = new Set();

      cornerstone.metaData.addProvider((type, imageId) => {
        if (type !== 'imagePlaneModule' || processing.has(imageId)) return undefined;

        processing.add(imageId);
        try {
          let base;
          base = cornerstone.metaData.get('imagePlaneModule', imageId);
          base = base || {};

          // Реальный in-plane spacing: из базовых метаданных, из массива pixelSpacing, иначе из картинки.
          let rs = base.rowPixelSpacing;
          let cs = base.columnPixelSpacing;

          if ((!rs || !cs) && Array.isArray(base.pixelSpacing) && base.pixelSpacing.length === 2) {
            rs = Number(base.pixelSpacing[0]);
            cs = Number(base.pixelSpacing[1]);
          }

          if (!rs || !cs) {
            const img = cornerstone.cache.getImage?.(imageId);
            if (img?.rowPixelSpacing && img?.columnPixelSpacing) {
              rs = img.rowPixelSpacing;
              cs = img.columnPixelSpacing;
            }
          }

          // Реального spacing нет → оставляем px (корректно)
          if (!(rs > 0 && cs > 0)) return undefined;

          // Уже правильно (реальный spacing + флаг снят) — не трогаем
          if (base.rowPixelSpacing === rs &&
              base.columnPixelSpacing === cs &&
              base.usingDefaultValues === false) {
            return undefined;
          }

          // КЛЮЧ: usingDefaultValues=false — снимаем флаг «дефолтного/некалиброванного» spacing
          return {
            ...base,
            rowPixelSpacing: rs,
            columnPixelSpacing: cs,
            pixelSpacing: [rs, cs],
            usingDefaultValues: false,
          };
        } finally {
          processing.delete(imageId);
        }
      }, CONFIG.IMAGE_PLANE_PROVIDER_PRIORITY);

      log.debug('[Init] imagePlaneModule spacing backfill provider installed');
    } catch (e) {
      log.warn('[Init] imagePlaneModule spacing provider failed:', e.message);
    }
  }

  /**
   * @private
   * @param {GPUInfo} gpuInfo 
   * @param {number} cacheSizeMB 
   * @param {boolean} [forceCPU=false] 
   */
  async _initCore(gpuInfo, cacheSizeMB, forceCPU = false) {
    const useCPU = forceCPU || !gpuInfo.webgl2;

    await cornerstone.init({
      rendering: {
        preferSizeOverAccuracy: gpuInfo.isMobile,
        useNorm16Texture: true,
        useCPURendering: useCPU,
      },
    });

    cornerstone.cache.setMaxCacheSize(cacheSizeMB * CONFIG.BYTES_IN_MB);
  }

  /**
   * @private
   */
  _initTools() {
    cornerstoneTools.init();
  }
}

// ══════════════════════════════════════════════════════════════
// DIRECT WebGL2 PROBE (fallback при падении GPU Detector)
// ══════════════════════════════════════════════════════════════

/**
 * @private
 * @returns {boolean}
 */
function _probeWebGL2Directly() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2', {
      powerPreference: 'high-performance',
      failIfMajorPerformanceCaveat: false,
    });

    if (!gl) {
      canvas.width = 0;
      canvas.height = 0;
      return false;
    }

    const result = gl.getParameter(gl.RENDERER) !== null;

    // Чистим
    const loseExt = gl.getExtension('WEBGL_lose_context');
    if (loseExt) {
      try { loseExt.loseContext(); } catch (e) { /* ignore */ }
    }
    canvas.width = 0;
    canvas.height = 0;

    return result;
  } catch (e) {
    return false;
  }
}

// ══════════════════════════════════════════════════════════════
// PUBLIC API
// ══════════════════════════════════════════════════════════════

const cornerstoneEngine = CornerstoneEngineManager.getInstance();

/**
 * Инициализация ядра Cornerstone3D.
 * Безопасна для многократного вызова (Race-condition proof).
 * 
 * @param {CornerstoneInitOptions} [options] 
 * @returns {Promise<CornerstoneContext>}
 */
export const initCornerstone = (options) => cornerstoneEngine.initialize(options);

/**
 * Проверка статуса инициализации.
 * @returns {boolean}
 */
export const isInitialized = () => cornerstoneEngine.isInitialized;

/**
 * Сброс состояния для повторной инициализации.
 */
export const resetCornerstone = () => cornerstoneEngine.reset();

// Реэкспорт библиотек
export { cornerstone, cornerstoneTools, dicomImageLoader };

// ══════════════════════════════════════════════════════════════
// ENTERPRISE DIAGNOSTICS
// ══════════════════════════════════════════════════════════════

/**
 * Полный диагностический отчёт для поддержки/отладки.
 * @returns {Object}
 */
export function getSystemDiagnostics() {
  const ctx = cornerstoneEngine.isInitialized ? cornerstoneEngine.context : null;
  const gpu = ctx?.gpuInfo || {};

  let cacheInfo = {};
  try {
    const maxSize = cornerstone.cache.getMaxCacheSize?.() || 0;
    const currentSize = cornerstone.cache.getCacheSize?.() || 0;
    cacheInfo = {
      maxSizeMB: Math.round(maxSize / CONFIG.BYTES_IN_MB),
      currentSizeMB: Math.round(currentSize / CONFIG.BYTES_IN_MB),
      usagePercent: maxSize > 0 ? Math.round((currentSize / maxSize) * 100) : 0,
    };
  } catch (e) {
    log.debug('[Init] Cache info unavailable:', e.message);
  }

  return {
    app: {
      version: '5.0.0',
      initialized: cornerstoneEngine.isInitialized,
      libraryVersions: getLibraryVersions(),
    },
    gpu: {
      renderer: gpu.renderer || gpu.tier?.label || 'Unknown',
      webgl2: !!gpu.webgl2,
      webgpu: !!gpu.webgpu,
      isMobile: !!gpu.isMobile,
      cores: gpu.cores || navigator.hardwareConcurrency || 'N/A',
      vramMB: gpu.vramEstimateMB || 'N/A',
      maxTextureSize: gpu.maxTextureSize || 'N/A',
    },
    cache: cacheInfo,
    browser: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      deviceMemoryGB: navigator.deviceMemory || 'N/A',
      screenRes: `${screen.width}×${screen.height}@${devicePixelRatio}x`,
      viewportSize: `${window.innerWidth}×${window.innerHeight}`,
    },
    features: {
      sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
      offscreenCanvas: typeof OffscreenCanvas !== 'undefined',
      webWorkers: typeof Worker !== 'undefined',
      indexedDB: typeof indexedDB !== 'undefined',
      speechRecognition: !!(window.SpeechRecognition || window.webkitSpeechRecognition),
      clipboard: !!navigator.clipboard,
    },
    timestamp: new Date().toISOString(),
  };
}

/**
 * Версии библиотек Cornerstone.
 * @returns {{cornerstone: string, tools: string, dicomLoader: string}}
 */
export function getLibraryVersions() {
  return {
    cornerstone: cornerstone.version || 'unknown',
    tools: cornerstoneTools.version || 'unknown',
    dicomLoader: dicomImageLoader.version || 'unknown',
  };
}